import React, { useMemo, useCallback } from 'react';
import { Box, Checkbox } from '@mui/material';

import { Plan, PlanTableProps } from '../../types/plan.types';
import { STATUS_TEXT, getStatusColors, ORDER_STATUS, getPlanRowStyle } from '../../styles';
import { enumToLabel, getPlacingMethodDisplayText } from '../../services/excel-table.service';

import BaseDataTable from '@/components/table/base-data-table';
import { ColumnConfig } from '@/components/types/types';
import { getCellStyle } from '@/components/styles/table-styles';

interface EnhancedPlanTableProps extends PlanTableProps {
  sortField?: string | null;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (field: string, order: 'asc' | 'desc') => void;
  hidePagination?: boolean;
  isMobile?: boolean;
}

const PlanTable: React.FC<EnhancedPlanTableProps> = ({
  onPlanClick,
  data = [],
  loading = false,
  currentPage = 1,
  itemsPerPage = 100,
  totalItems = 0,
  selectedPlans = [],
  onPageChange,
  onItemsPerPageChange,
  onSelectionChange: _onSelectionChange,
  sortField = 'placementDate',
  sortOrder = 'desc',
  onSortChange,
  hidePagination = false,
  isMobile = false,
}) => {
  const dataCanUpdate = useMemo(() => data.filter((plan) => plan.canUpdate), [data]);
  const visibleIds = useMemo(() => dataCanUpdate.map((plan) => plan.id), [dataCanUpdate]);

  const selectedOnPageCount = useMemo(
    () => visibleIds.filter((id) => selectedPlans.includes(id)).length,
    [visibleIds, selectedPlans]
  );

  const isAllSelected = useMemo(
    () => dataCanUpdate.length > 0 && selectedOnPageCount === dataCanUpdate.length,
    [dataCanUpdate, selectedOnPageCount]
  );

  const isIndeterminate = useMemo(
    () => selectedOnPageCount > 0 && selectedOnPageCount < dataCanUpdate.length,
    [selectedOnPageCount, dataCanUpdate]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      // If in indeterminate state (some selected), deselect current page rows.
      if (isIndeterminate) {
        const newSelections = selectedPlans.filter((id) => !visibleIds.includes(id));
        _onSelectionChange?.(newSelections);
        return;
      }

      if (checked) {
        // Select all rows on current page and keep selections from other pages.
        const newSelections = [...selectedPlans];
        visibleIds.forEach((id) => {
          if (!newSelections.includes(id)) {
            newSelections.push(id);
          }
        });
        _onSelectionChange?.(newSelections);
      } else {
        // Deselect only rows on current page.
        const newSelections = selectedPlans.filter((id) => !visibleIds.includes(id));
        _onSelectionChange?.(newSelections);
      }
    },
    [isIndeterminate, selectedPlans, visibleIds, _onSelectionChange]
  );

  const handleSelectPlan = useCallback(
    (planId: string, checked: boolean) => {
      if (checked) {
        if (!selectedPlans.includes(planId)) {
          _onSelectionChange?.([...selectedPlans, planId]);
        }
        return;
      } else {
        _onSelectionChange?.(selectedPlans.filter((id) => id !== planId));
      }
    },
    [selectedPlans, _onSelectionChange]
  );

  // Get status border color for left border indicator
  const getStatusColor = useCallback((orderStatus: number) => {
    switch (orderStatus) {
      case ORDER_STATUS.UNSPECIFIED: // 指定なし
        return '#E0E0E0'; // Light gray border
      case ORDER_STATUS.NEGOTIATING: // 交渉中 (High Priority)
        return '#D32F2F'; // Red border
      case ORDER_STATUS.ARRANGED: // 手配済 (Active)
        return '#0047BA'; // Blue border
      default:
        return '#E0E0E0';
    }
  }, []);

  // Define columns based on design
  const columns: ColumnConfig<Plan>[] = useMemo(() => {
    const allColumns: ColumnConfig<Plan>[] = [
      {
        key: 'select',
        label: '',
        width: { xs: '3%', sm: '3%', md: '3%' },
        renderHeader: () => (
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={(e) => {
              handleSelectAll(e.target.checked);
            }}
            size='small'
          />
        ),
        render: (_, plan: Plan) => (
          <Box onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selectedPlans.includes(plan.id)}
              disabled={!plan.canUpdate}
              onChange={(e) => {
                e.stopPropagation();
                handleSelectPlan(plan.id, e.target.checked);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              size='small'
            />
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'placementDate',
        label: '予定日',
        width: { xs: '32%', sm: '7%', md: '7%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.placementDate}>
            {plan.placementDate}
          </Box>
        ),
        sortable: true,
        sortField: 'placementDate',
        useInMobile: true,
      },
      {
        key: 'timePossible',
        label: '可能時間',
        width: { xs: '6%', sm: '6%', md: '6%' },
        render: (_, plan: Plan) => (
          <Box
            sx={getCellStyle('100%')}
            title={`${plan.timePossible?.slice(0, 5) || ''}${plan.timePossibleRemarks || ''}`}
          >
            {plan.timePossible?.slice(0, 5) || ''}
            {plan.timePossibleRemarks || ''}
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'personInChargeName',
        label: '営業',
        width: { xs: '6%', sm: '6%', md: '6%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.personInChargeName}>
            {plan.personInChargeName}
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'abbreviationName',
        label: 'プラント (仕入先)',
        width: { xs: '8%', sm: '8%', md: '8%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.abbreviationName}>
            {plan.abbreviationName}
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'constructionCompanyNameAbbreviation',
        label: '建設会社',
        width: { xs: '33%', sm: '7%', md: '7%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.constructionCompanyNameAbbreviation}>
            {plan.constructionCompanyNameAbbreviation}
          </Box>
        ),
        sortable: false,
        useInMobile: true,
      },
      {
        key: 'propertyNameAbbreviation',
        label: '現場略称',
        width: { xs: '32%', sm: '7%', md: '7%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.propertyNameAbbreviation}>
            {plan.propertyNameAbbreviation}
          </Box>
        ),
        sortable: false,
        useInMobile: true,
      },
      {
        key: 'placeToPlace',
        label: '打設箷所',
        width: { xs: '6%', sm: '6%', md: '6%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.placeToPlace || ''}>
            {plan.placeToPlace || ''}
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'vehicleSize',
        label: '車両',
        width: { xs: '4%', sm: '4%', md: '4%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.vehicleSize?.toString()}>
            {enumToLabel.vehicleSize(plan.vehicleSize)}
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'quantity',
        label: '数量',
        width: { xs: '6%', sm: '6%', md: '6%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')}>
            {plan.quantity?.toLocaleString()}
            {' ' + enumToLabel.quantitySuffix(Number(plan.quantitySuffix))}
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'precedingMaterial',
        label: '先行材 数量',
        width: { xs: '7%', sm: '7%', md: '7%' },
        render: (_, plan: Plan) => (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
            title={`${plan.precedingMaterialType || ''}\n${plan.precedingMaterialQuantity || ''}`}
          >
            <Box sx={{ ...getCellStyle('100%'), borderBottom: 'none' }}>
              {enumToLabel.materialType(Number(plan.precedingMaterialType))}
            </Box>
            <Box sx={getCellStyle('100%')}>
              {plan.precedingMaterialQuantity
                ? plan.precedingMaterialQuantity.toLocaleString()
                : ''}
            </Box>
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'formulationProduct',
        label: '配合・商品',
        width: { xs: '7%', sm: '7%', md: '7%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.formulationProduct}>
            {plan.formulationProduct}
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'pitchInfo',
        label: '希望 可能ピッチ',
        width: { xs: '7%', sm: '7%', md: '7%' },
        render: (_, plan: Plan) => (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
            title={`${plan.pitchHope || ''}\n${plan.pitchPossible || ''}`}
          >
            <Box sx={{ ...getCellStyle('100%'), borderBottom: 'none' }}>{plan.pitchHope || ''}</Box>
            <Box sx={getCellStyle('100%')}>{plan.pitchPossible || ''}</Box>
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'placingMethod',
        label: '打設方法',
        width: { xs: '6%', sm: '6%', md: '6%' },
        render: (_, plan: Plan) => {
          const displayText = getPlacingMethodDisplayText(
            plan.placingMethod,
            plan.placingMethodOthers
          );
          return (
            <Box sx={getCellStyle('100%')} title={displayText}>
              {displayText}
            </Box>
          );
        },
        sortable: false,
      },
      {
        key: 'witnessCompanyName',
        label: '立会会社',
        width: { xs: '8%', sm: '8%', md: '8%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.witnessCompanyName || ''}>
            {plan.witnessCompanyName || ''}
          </Box>
        ),
        sortable: false,
      },
      {
        key: 'orderStatus',
        label: 'ステータス',
        width: { xs: '8%', sm: '8%', md: '8%' },
        render: (_, plan: Plan) => {
          // Priority 2: Order status with confirmation sub-status
          const statusText =
            STATUS_TEXT[plan.orderStatus as keyof typeof STATUS_TEXT] ||
            plan.orderStatus?.toString() ||
            '';
          const borderColor = getStatusColor(plan.orderStatus);
          const backgroundColor = getStatusColor(plan.orderStatus);

          // Determine if this status can have confirmation sub-status
          const canHaveConfirmation =
            plan.orderStatus === ORDER_STATUS.ARRANGED ||
            plan.orderStatus === ORDER_STATUS.NEGOTIATING;

          // Determine confirmation sub-status
          let confirmationText = '';
          let confirmationBgColor = '';
          let confirmationTextColor = '#FFFFFF';

          if (canHaveConfirmation) {
            if (plan.isPreconfirmed) {
              confirmationText = '前日確認';
              confirmationBgColor = '#E6E0FF';
              confirmationTextColor = '#1976D2';
            } else {
              confirmationText = '未確認';
              confirmationBgColor = '#FFDAD6';
              confirmationTextColor = '#BA1A1A';
            }
          }

          return (
            <Box
              sx={{
                ...getCellStyle('100%'),
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                alignItems: 'center',
              }}
              title={`${statusText}${confirmationText ? ' - ' + confirmationText : ''}`}
            >
              {/* Main status badge */}
              <Box
                sx={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: '20px',
                  backgroundColor: backgroundColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px 8px',
                  minWidth: '60px',
                  fontSize: '10px',
                  fontWeight: 500,
                  color: plan.orderStatus === ORDER_STATUS.UNSPECIFIED ? '#000000' : '#FFFFFF',
                }}
              >
                {statusText}
              </Box>

              {/* Confirmation sub-status badge */}
              {canHaveConfirmation && confirmationText && (
                <Box
                  sx={{
                    border: `1px solid ${confirmationBgColor}`,
                    borderRadius: '20px',
                    backgroundColor: confirmationBgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2px 6px',
                    minWidth: '50px',
                    fontSize: '9px',
                    fontWeight: 500,
                    color: confirmationTextColor,
                  }}
                >
                  {confirmationText}
                </Box>
              )}
            </Box>
          );
        },
        sortable: false,
      },
      {
        key: 'remarks',
        label: '社内備考',
        width: { xs: '5%', sm: '5%', md: '5%' },
        render: (_, plan: Plan) => (
          <Box sx={getCellStyle('100%')} title={plan.remarks ? '有' : '無'}>
            {plan.remarks ? '有' : '無'}
          </Box>
        ),
        sortable: false,
      },
    ];

    // Filter columns if isMobile is provided
    if (isMobile) {
      return allColumns.filter((col) => col.useInMobile);
    }

    return allColumns;
  }, [
    isAllSelected,
    isIndeterminate,
    selectedPlans,
    handleSelectAll,
    handleSelectPlan,
    getStatusColor,
    isMobile,
  ]);

  // Row style based on selection, archive status, and order status
  const getRowStyle = useCallback(
    (plan: Plan): React.CSSProperties => {
      const borderColor = getStatusColor(plan.orderStatus);
      const statusBgColor =
        getStatusColors(!!plan.isPreconfirmed)[
          plan.orderStatus as keyof ReturnType<typeof getStatusColors>
        ] || '#FFFFFF';
      let isPast = false;
      if (plan.placementDate) {
        const today = new Date();
        const date = new Date(plan.placementDate);
        isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
      }
      return getPlanRowStyle({
        isSelected: selectedPlans.includes(plan.id),
        isPast,
        isArchive: plan.isArchive,
        isPreconfirmed: !!plan.isPreconfirmed,
        borderColor,
        statusBgColor,
      });
    },
    [selectedPlans, getStatusColor]
  );

  // Handle row click
  const handleRowClick = useCallback(
    (plan: Plan) => {
      onPlanClick?.(plan);
    },
    [onPlanClick]
  );

  return (
    <BaseDataTable<Plan>
      data={data}
      loading={loading}
      columns={columns}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onItemsPerPageChange={onItemsPerPageChange}
      onRowClick={handleRowClick}
      sortField={sortField}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
      getRowStyle={getRowStyle}
      hidePagination={hidePagination}
    />
  );
};

export default PlanTable;
